import React, { useState, useEffect } from 'react';
import { useCart } from '../../hooks/useCart';
import { useNavigate } from 'react-router-dom';
import styled,  { keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import PostcodeCheck from '../common/PostcodeCheck';
import { CreditCard, DollarSign, User, Phone, MapPin, Home, Building, Flag, Mail } from 'lucide-react';
import Cart from './Cart';
import { loadStripe } from '@stripe/stripe-js';

// Add keyframes for button loading animation
const loadingAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;


const PageBackground = styled.div`
  background-color: #f8f9fa;
  min-height: 100vh;
  padding: 40px 0;
`;

const CheckoutContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background-color: #ffffff;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 32px;
  color: #2c3e50;
  margin-bottom: 30px;
  text-align: center;
  font-weight: 700;
`;

const FormSection = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  color: #34495e;
  margin-bottom: 20px;
  font-weight: 600;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  position: relative;
`;

const FormLabel = styled.label`
  font-size: 14px;
  color: #7f8c8d;
  margin-bottom: 5px;
  display: block;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 16px;
  transition: border-color 0.3s ease;

  &:focus {
    border-color: #3498db;
    outline: none;
    box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
  }
`;

const InputIcon = styled.span`
  position: absolute;
  left: 12px;
  top: 38px;
  color: #95a5a6;
`;

const PaymentOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 30px;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const PostcodeStatus = styled.span`
  font-size: 14px;
  margin-left: 10px;
  color: ${props => props.isEligible ? '#2ecc71' : '#e74c3c'};
`;

const PayButton = styled.button`
  background-color: ${({ type }) => (type === 'delivery' ? '#2ecc71' : '#3498db')};
  color: white;
  padding: 10px 16px;
  font-size: 14px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 250px;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  &:hover {
    background-color: ${({ type }) => (type === 'delivery' ? '#27ae60' : '#2980b9')};
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }

  @media (min-width: 768px) {
    width: 48%;
    font-size: 16px;
    padding: 12px 24px;
  }
`;

const ButtonText = styled.span`
  @media (max-width: 400px) {
   
  }
`;
const LoadingSpinner = styled.div`
  border: 4px solid #f3f3f3;
  border-radius: 50%;
  border-top: 4px solid #3498db;
  width: 18px;
  height: 18px;
  animation: ${loadingAnimation} 2s linear infinite;
`;

const Checkout = () => {
  const { cartItems, clearCart } = useCart();
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');  
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postcode, setPostcode] = useState('');
  const [postcodeEligible, setPostcodeEligible] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);  
  const [isSubmitting, setIsSubmitting] = useState(false);  // For button animation
  const [stripePromise, setStripePromise] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch the publishable key from the backend
    fetch('/api/config/')
      .then((res) => res.json())
      .then((data) => {
        const { stripePublishableKey } = data;
        setStripePromise(loadStripe(stripePublishableKey));
      })
      .catch((error) => {
        console.error('Error fetching Stripe publishable key:', error);
        toast.error('Failed to load payment system. Please try again later.');
      });
  }, []);
  // Check if the postcode is eligible
  useEffect(() => {
    if (postcode.length === 4) {
      checkPostcode(postcode);
    } else {
      setPostcodeEligible(false);
    }
  }, [postcode]);

  // Update form validity
  useEffect(() => {
    const isFormFilled = name && email && phoneNumber && addressLine1 && city && state && postcode && postcodeEligible;
    const isCartNotEmpty = cartItems.length > 0;
    setIsFormValid(isFormFilled && isCartNotEmpty);
  }, [name, email, phoneNumber, addressLine1, city, state, postcode, postcodeEligible, cartItems]);

  const normalizePhoneNumber = (phoneNumber) => {
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.startsWith('61')) {
      return `+${cleaned}`; 
    } else if (cleaned.startsWith('0')) {
      return `+61${cleaned.slice(1)}`;
    } else {
      return `+61${cleaned}`;
    }
  };

  const checkPostcode = async (code) => {
    try {
      const response = await fetch('/api/check-postcode/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ postcode: code }),
      });
      const data = await response.json();
      if (response.ok) {
        setPostcodeEligible(data.is_eligible);
        toast[data.is_eligible ? 'success' : 'error'](
          data.is_eligible ? 'Delivery available' : 'Delivery unavailable',
          { autoClose: 3000 }
        );
      } else {
        throw new Error(data.error || 'Failed to check postcode');
      }
    } catch (error) {
      toast.error(error.message);
      setPostcodeEligible(false);
    }
  };

  const handleCheckout = async (paymentMethod) => {
    setIsSubmitting(true);

    const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
    const checkoutData = {
      customer: {
        name,
        email,
        phone_number: normalizedPhoneNumber,
        address_line_1: addressLine1,
        address_line_2: addressLine2,
        city,
        state,
        postcode,
      },
      items: cartItems.map(item => ({
        product_id: item.id,
        quantity: item.isPrePacked ? item.quantity : item.selectedWeight,
      })),
      total_price: cartItems.reduce((sum, item) => sum + item.price * (item.isPrePacked ? item.quantity : item.selectedWeight), 0),
      payment_method: paymentMethod,
    };
  
    try {
      const response = await fetch('/api/checkout/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(checkoutData),
      });

      if (!response.ok) {
        throw new Error('Failed to place the order.');
      }

      const orderData = await response.json();

      if (paymentMethod === 'card') {
        if (orderData.sessionId) {
          let stripe;
          if (stripePromise instanceof Promise) {
            stripe = await stripePromise;
          } else {
            stripe = stripePromise;
          }
  
          if (stripe) {
            // Store the order ID in localStorage before redirecting
            localStorage.setItem('pendingOrderId', orderData.custom_order_id);
            const { error } = await stripe.redirectToCheckout({
              sessionId: orderData.sessionId
            });
            
            if (error) {
              console.error('Stripe redirect error:', error);
              toast.error('Payment process failed. Please try again.');
            }
          } else {
            throw new Error('Stripe is not initialized');
          }
        } else {
          throw new Error('Stripe session ID not received');
        }
      } else {
        // Cash payment
        clearCart();
        toast.success('Order placed successfully!');
        navigate('/thank-you', { state: { orderId: orderData.custom_order_id } });
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.message || 'Failed to place the order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <PageBackground>
      <CheckoutContainer>
        <Title>Guest Checkout</Title>
        
        <FormSection>
          <PostcodeCheck onEligible={setPostcodeEligible} />
        </FormSection>

        <FormSection>
          <Cart showCheckoutButton={false} showToast={toast} onProceedToCheckout={() => {}} />
        </FormSection>
                
        <FormSection>
          <SectionTitle>Contact Information</SectionTitle>
          <FormGroup>
            <FormLabel>Full Name</FormLabel>
            <InputIcon><User size={18} /></InputIcon>
            <FormInput
              type="text"
              placeholder="John Doe"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>Email</FormLabel>
            <InputIcon><Mail size={18} /></InputIcon>
            <FormInput
              type="email"
              placeholder="johndoe@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>Phone Number</FormLabel>
            <InputIcon><Phone size={18} /></InputIcon>
            <FormInput
              type="tel"
              placeholder="0400 000 000"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              required
            />
          </FormGroup>
        </FormSection>

        <FormSection>
          <SectionTitle>Delivery Address</SectionTitle>
          <FormGroup>
            <FormLabel>Address Line 1</FormLabel>
            <InputIcon><MapPin size={18} /></InputIcon>
            <FormInput
              type="text"
              placeholder="123 Main St"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              required
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>Address Line 2</FormLabel>
            <InputIcon><Home size={18} /></InputIcon>
            <FormInput
              type="text"
              placeholder="Apt 4B (Optional)"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>City</FormLabel>
            <InputIcon><Building size={18} /></InputIcon>
            <FormInput
              type="text"
              placeholder="Melbourne"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              required
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>State</FormLabel>
            <InputIcon><Flag size={18} /></InputIcon>
            <FormInput
              type="text"
              placeholder="Victoria"
              value={state}
              onChange={(e) => setState(e.target.value)}
              required
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>
              Postcode
              {postcode.length === 4 && (
                <PostcodeStatus isEligible={postcodeEligible}>
                  {postcodeEligible ? '(Eligible for delivery)' : '(Not eligible for delivery)'}
                </PostcodeStatus>
              )}
            </FormLabel>
            <InputIcon><MapPin size={18} /></InputIcon>
            <FormInput
              type="text"
              placeholder="3000"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              required
            />
          </FormGroup>
        </FormSection>

        <PaymentOptions>
          <PayButton 
            type="delivery" 
            onClick={() => handleCheckout('cash')}
            disabled={!isFormValid || isSubmitting} // Disable button if form is not valid or submitting
          >
            {isSubmitting ? <LoadingSpinner /> : <DollarSign size={20} />}
            <ButtonText>{isSubmitting ? 'Processing...' : 'Pay on Delivery'}</ButtonText>
          </PayButton>
          <PayButton 
            type="card" 
            onClick={() => handleCheckout('card')}
            disabled={!isFormValid || isSubmitting} // Disable button if form is not valid or submitting
          >
            {isSubmitting ? <LoadingSpinner /> : <CreditCard size={20} />}
            <ButtonText>{isSubmitting ? 'Processing...' : 'Pay by Card'}</ButtonText>
          </PayButton>
        </PaymentOptions>
      </CheckoutContainer>
    </PageBackground>
  );
};

export default Checkout;


